import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { ArrowLeft2, ArrowRight2, Add, Minus, DocumentDownload } from "iconsax-react";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { Loader } from "@/shared/components";
import { ModalWrapper, ButtonGroup, Button } from "@/shared/ui";
import { proxyS3Url } from "@/shared/utils/proxyS3Url";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DOCUMENT_OPTIONS = { withCredentials: false };

interface Props {
  fileUrl: string;
  fileName?: string;
  title?: string;
  secondaryFileUrl?: string;
  secondaryFileName?: string;
  onClose: () => void;
}

export default function PdfViewer({ fileUrl, fileName, title, secondaryFileUrl, secondaryFileName, onClose }: Props) {
  const { t } = useTranslation("Common");
  const [activeFile, setActiveFile] = useState<"primary" | "secondary">("primary");
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [error, setError] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingByButton = useRef(false);

  const hasTabs = Boolean(secondaryFileUrl);
  const currentFileUrl = proxyS3Url(activeFile === "primary" ? fileUrl : secondaryFileUrl!);
  const currentFileName = activeFile === "primary" ? fileName : secondaryFileName;

  const tabOptions = useMemo(
    () => [
      { label: t("pdfViewer.signedVersion"), value: "primary" },
      { label: t("pdfViewer.unsignedVersion"), value: "secondary" },
    ],
    [t],
  );

  function handleTabChange(value: string) {
    setActiveFile(value as "primary" | "secondary");
    setNumPages(0);
    setPageNumber(1);
    setScale(1.0);
    setError(false);
  }

  const isLoaded = numPages > 0;
  const displayName =
    currentFileName || decodeURIComponent(currentFileUrl.split("/").pop()?.split("?")[0] ?? "document.pdf");

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  function getPageElements() {
    const container = scrollContainerRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLDivElement>("[data-page-wrapper]"));
  }

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || numPages === 0) return;

    function handleScroll() {
      if (isScrollingByButton.current) return;

      const pages = getPageElements();
      if (pages.length === 0) return;

      const containerRect = container!.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      let closestPage = 1;
      let closestDistance = Infinity;

      pages.forEach((page, index) => {
        const rect = page.getBoundingClientRect();
        const pageCenter = rect.top + rect.height / 2;
        const distance = Math.abs(pageCenter - containerCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = index + 1;
        }
      });

      setPageNumber(closestPage);
    }

    container.addEventListener("scroll", handleScroll, { passive: true });
    const initialTimeout = setTimeout(handleScroll, 200);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      clearTimeout(initialTimeout);
    };
  }, [numPages, scale]);

  function scrollToPage(page: number) {
    const pages = getPageElements();
    const target = pages[page - 1];
    if (!target) return;

    isScrollingByButton.current = true;
    setPageNumber(page);
    target.scrollIntoView({ behavior: "instant", block: "start" });

    setTimeout(() => {
      isScrollingByButton.current = false;
    }, 200);
  }

  function handlePrevPage() {
    scrollToPage(Math.max(pageNumber - 1, 1));
  }

  function handleNextPage() {
    scrollToPage(Math.min(pageNumber + 1, numPages));
  }

  function handleZoomIn() {
    setScale((prev) => Math.min(prev + 0.25, 3));
  }

  function handleZoomOut() {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }

  function handleDownload() {
    const link = document.createElement("a");
    link.href = currentFileUrl;
    link.download = displayName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <ModalWrapper onClose={onClose} width="w-7xl">
      <p className="text-heading-sm content-base-primary truncate pr-8 shrink-0">{title || displayName}</p>

      {hasTabs && (
        <div className="flex shrink-0">
          <ButtonGroup options={tabOptions} value={activeFile} onChange={handleTabChange} />
        </div>
      )}

      {isLoaded && (
        <div className="flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={pageNumber <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowLeft2 size={16} color="currentColor" />
            </button>
            <span className="text-label-sm content-base-secondary px-2">
              {pageNumber} / {numPages}
            </span>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={pageNumber >= numPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowRight2 size={16} color="currentColor" />
            </button>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={scale <= 0.5}
              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
              <Minus size={16} color="currentColor" />
            </button>
            <span className="text-label-sm content-base-secondary px-2">{Math.round(scale * 100)}%</span>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={scale >= 3}
              className="w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer content-base-secondary hover:surface-tertiary-fill disabled:opacity-40 disabled:cursor-not-allowed">
              <Add size={16} color="currentColor" />
            </button>
            <Button size="md" onClick={handleDownload}>
              <DocumentDownload size={16} color="currentColor" />
              {t("pdfViewer.download")}
            </Button>
          </div>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="component-scroll flex flex-col items-center gap-4 flex-1 min-h-0 rounded-2xl surface-component-fill p-1">
        {error ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-body-regular-md content-base-secondary">{t("pdfViewer.loadError")}</p>
          </div>
        ) : (
          <Document
            key={currentFileUrl}
            file={currentFileUrl}
            options={DOCUMENT_OPTIONS}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() => setError(true)}
            loading={
              <div className="flex items-center justify-center py-20 text-brand-500">
                <Loader />
              </div>
            }>
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} data-page-wrapper className={i < numPages - 1 ? "bg-white mb-4" : "bg-white"}>
                <Page pageNumber={i + 1} scale={scale} renderTextLayer renderAnnotationLayer />
              </div>
            ))}
          </Document>
        )}
      </div>
    </ModalWrapper>
  );
}
